import { useEffect, useMemo, type ComponentType, type MouseEvent } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { selectCurrentOrganizationId, setCurrentOrganization, hasPermission } from '@state/permissions-slice';
import { selectSelectedOrganizationId } from '@state/app-context-slice';
import { createNavigationEntry } from '@brz/brz-frame';

import { Permissions } from '@auth/permissions';
import { RootState } from '@state/root-state';
import type { SvgIconProps } from '@mui/material/SvgIcon';
import type { NavigationEntry } from '@brz/brz-frame';

import DomainOutlined from '@mui-symbols-material/w400/DomainOutlined';
import HandshakeOutlined from '@mui-symbols-material/w400/HandshakeOutlined';
import PinOutlined from '@mui-symbols-material/w400/PinOutlined';
import ArticleOutlined from '@mui-symbols-material/w400/ArticleOutlined';
import WavingHandOutlined from '@mui-symbols-material/w400/WavingHandOutlined';
import WorkOutlined from '@mui-symbols-material/w400/WorkOutlined';
import SquareFootOutlined from '@mui-symbols-material/w400/SquareFootOutlined';
import AddCardOutlined from '@mui-symbols-material/w400/AddCardOutlined';
import ImageOutlined from '@mui-symbols-material/w400/ImageOutlined';

const playgroundEnabled = import.meta.env.VITE_PLAYGROUND_ENABLED === 'true';
const translationEditorEnabled = import.meta.env.VITE_TRANSLATION_EDITOR_ENABLED === 'true' || false;

interface MenuItemConfig {
  id: string;
  key: string;
  path: string;
  permissionId?: string;
  icon?: ComponentType<SvgIconProps>;
}

const initialMenuItems: MenuItemConfig[] = [
  { id: '/organizations', key: 'organizations', path: '/organizations', permissionId: Permissions.OrganizationRead, icon: DomainOutlined },
  {
    id: '/business-partners',
    key: 'businessPartners',
    path: '/business-partners',
    permissionId: Permissions.BusinessPartnersRead,
    icon: HandshakeOutlined
  },
  ...(playgroundEnabled ? [{ id: '/playground', key: 'playground', path: '/playground' }] : []),
  ...(translationEditorEnabled ? [{ id: '/translation-editor', key: 'translationEditor', path: '/translation-editor' }] : []),
  { id: '/numberRanges', key: 'numberRanges', path: '/number-ranges', permissionId: Permissions.NumberRangesRead, icon: PinOutlined },
  { id: '/texts', key: 'texts', path: '/texts', permissionId: Permissions.TextsRead, icon: ArticleOutlined },
  { id: '/salutations', key: 'salutations', path: '/salutations', permissionId: Permissions.SalutationsRead, icon: WavingHandOutlined },
  { id: '/functions', key: 'functions', path: '/functions', permissionId: Permissions.FunctionsRead, icon: WorkOutlined },
  { id: '/quantityUnits', key: 'quantityUnits', path: '/quantity-units', permissionId: Permissions.QuantityUnitsRead, icon: SquareFootOutlined },
  { id: '/freeFields', key: 'freeFields', path: '/free-fields', permissionId: Permissions.CustomFieldsRead, icon: AddCardOutlined },
  { id: '/reportImages', key: 'reportImages', path: '/report-images', permissionId: Permissions.ReportImagesRead, icon: ImageOutlined }
];

export function useMenuItems(): NavigationEntry[] {
  const { t } = useTranslation(['layouts']);
  const dispatch = useDispatch();

  const appContextOrgId = useSelector(selectSelectedOrganizationId);
  const permissionsOrgId = useSelector(selectCurrentOrganizationId);

  useEffect(() => {
    if (appContextOrgId && appContextOrgId !== permissionsOrgId) {
      dispatch(setCurrentOrganization(appContextOrgId));
    }
  }, [appContextOrgId, permissionsOrgId, dispatch]);

  const organizationId = permissionsOrgId;

  const organizationRead = useSelector((state: RootState) => hasPermission(state, Permissions.OrganizationRead));
  const businessPartnersRead = useSelector((state: RootState) => hasPermission(state, Permissions.BusinessPartnersRead));
  const numberRangesRead = useSelector((state: RootState) => hasPermission(state, Permissions.NumberRangesRead));
  const textsRead = useSelector((state: RootState) => hasPermission(state, Permissions.TextsRead));
  const salutationsRead = useSelector((state: RootState) => hasPermission(state, Permissions.SalutationsRead));
  const functionsRead = useSelector((state: RootState) => hasPermission(state, Permissions.FunctionsRead));
  const quantityUnitsRead = useSelector((state: RootState) => hasPermission(state, Permissions.QuantityUnitsRead));
  const customFieldsRead = useSelector((state: RootState) => hasPermission(state, Permissions.CustomFieldsRead));
  const reportImagesRead = useSelector((state: RootState) => hasPermission(state, Permissions.ReportImagesRead));

  const filteredMenuItems = useMemo(() => {
    const evaluatedPermissions = {
      [Permissions.OrganizationRead]: organizationRead,
      [Permissions.BusinessPartnersRead]: businessPartnersRead,
      [Permissions.NumberRangesRead]: numberRangesRead,
      [Permissions.TextsRead]: textsRead,
      [Permissions.SalutationsRead]: salutationsRead,
      [Permissions.FunctionsRead]: functionsRead,
      [Permissions.QuantityUnitsRead]: quantityUnitsRead,
      [Permissions.CustomFieldsRead]: customFieldsRead,
      [Permissions.ReportImagesRead]: reportImagesRead
    };
    return initialMenuItems.filter(item => {
      if (!item.permissionId) {
        return true;
      }
      return !!organizationId && evaluatedPermissions[item.permissionId as keyof typeof evaluatedPermissions];
    });
  }, [
    organizationId,
    organizationRead,
    businessPartnersRead,
    numberRangesRead,
    textsRead,
    salutationsRead,
    functionsRead,
    quantityUnitsRead,
    customFieldsRead,
    reportImagesRead
  ]);

  return useMemo(
    () =>
      filteredMenuItems.map(item => {
        const IconComponent = item.icon;
        return createNavigationEntry({
          title: t(`mainMenu.${item.key}`),
          icon: IconComponent ? <IconComponent /> : undefined,
          activePath: item.path,
          component: Link,
          componentProps: {
            to: item.path,
            onClick: (event: MouseEvent<HTMLAnchorElement>): void => {
              if (!organizationId) {
                event.preventDefault();
              }
            }
          }
        });
      }),
    [filteredMenuItems, organizationId, t]
  );
}
